import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { toast } from 'sonner';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  maxStock?: number;
  originalPrice?: number;
  inStoreOnly?: boolean;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (item: Omit<CartItem, 'quantity'> & { maxStock?: number; originalPrice?: number; inStoreOnly?: boolean }) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  lastAddedItem: string | null;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const savedItems = localStorage.getItem('cartItems');
      return savedItems ? JSON.parse(savedItems) : [];
    } catch (error) {
      console.error('Failed to parse cart items:', error);
      return [];
    }
  });

  // Save to localStorage whenever items change
  React.useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(items));
  }, [items]);

  // Verify stock availability when cart loads from localStorage
  React.useEffect(() => {
    const verifyStock = async () => {
      if (items.length === 0) return;
      try {
        const response = await fetch('/api/products');
        if (!response.ok) return;
        const products = await response.json();
        
        setItems(currentItems => {
          let hasChanges = false;
          const verified = currentItems.map(item => {
            const product = products.find((p: any) => p.id === item.id);
            if (!product) {
              hasChanges = true;
              return null; // Product no longer exists
            }
            // Zamień na liczbę aby uniknąć problemów z stringami ("0")
            const currentStock = product.stock !== null && product.stock !== undefined ? Number(product.stock) : Infinity;
            let updatedItem = { ...item };
            let itemChanged = false;
            
            if (item.quantity > currentStock) {
              itemChanged = true;
              if (currentStock <= 0) {
                hasChanges = true; // MUST FLAG BEFORE RETURN!
                return null; // Out of stock
              }
              updatedItem.quantity = currentStock;
            }
            
            if (updatedItem.price !== product.price) {
              itemChanged = true;
              updatedItem.price = product.price;
            }
            
            if (updatedItem.originalPrice !== product.originalPrice) {
              itemChanged = true;
              updatedItem.originalPrice = product.originalPrice;
            }
            
            if (itemChanged) {
              hasChanges = true;
            }

            return { ...updatedItem, maxStock: currentStock };
          }).filter(Boolean) as CartItem[];

          if (hasChanges) {
            toast.info(
              'Zaktualizowaliśmy Twój koszyk — niektóre produkty zmieniły cenę lub dostępność.',
              { duration: 4000 }
            );
          }
          return hasChanges ? verified : currentItems;
        });
      } catch {
        // silently fail - cart will still work with cached data
      }
    };
    verifyStock();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [lastAddedItem, setLastAddedItem] = useState<string | null>(null);

  const addToCart = useCallback((newItem: Omit<CartItem, 'quantity'> & { maxStock?: number; originalPrice?: number }) => {
    setItems(currentItems => {
      const existingItem = currentItems.find(item => item.id === newItem.id);

      if (existingItem) {
        // Check stock limit for existing item
        if (newItem.maxStock !== undefined && existingItem.quantity + 1 > newItem.maxStock) {
          toast.error('Osiągnięto maksymalną ilość dostępną w magazynie');
          return currentItems;
        }

        return currentItems.map(item =>
          item.id === newItem.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }

      // Check stock limit for new item (should check against 1)
      if (newItem.maxStock !== undefined && 1 > newItem.maxStock) {
        toast.error('Produkt niedostępny w magazynie');
        return currentItems;
      }

      return [...currentItems, { ...newItem, quantity: 1 }];
    });

    // Set animation trigger
    setLastAddedItem(newItem.id);
    setTimeout(() => setLastAddedItem(null), 500);

    // Show toast notification
    if (!newItem.maxStock || 1 <= newItem.maxStock) {
      toast.success(`${newItem.name} dodano do koszyka!`, {
        duration: 2000,
        position: 'bottom-right',
      });
    }
  }, []);

  const removeFromCart = (id: string) => {
    setItems(currentItems => currentItems.filter(item => item.id !== id));
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }

    setItems(currentItems => {
      return currentItems.map(item => {
        if (item.id === id) {
          // Check stock limit
          if (item.maxStock !== undefined && quantity > item.maxStock) {
            toast.error('Osiągnięto maksymalną ilość dostępną w magazynie');
            return { ...item, quantity: item.maxStock };
          }
          return { ...item, quantity };
        }
        return item;
      });
    });
  };

  const clearCart = () => {
    setItems([]);
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
        isCartOpen,
        setIsCartOpen,
        lastAddedItem,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
